import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import TrustIndicators from './components/TrustIndicators';
import CookieConsent from '../../components/CookieConsent';
import Footer from '../../components/ui/Footer';

const UserLogin = () => {
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { signInWithOAuth, loading, user } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/user-dashboard');
    }
  }, [user, navigate]);

  // Clear any existing errors when component mounts
  useEffect(() => {
    setError(null);
  }, []);

  const handleSocialLogin = async (provider) => {
    setError(null);

    try {
      // Only Google OAuth is supported
      if (provider.toLowerCase() !== 'google') {
        setError(`Unsupported provider: ${provider}`);
        return;
      }

      const { error: oauthError } = await signInWithOAuth('google');

      if (oauthError) {
        setError(oauthError);
      }
    } catch (err) {
      setError('Google login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Unified Hero Section - Seamless Background */}
      <div className="flex-1 relative min-h-screen">

        {/* Single Animated Gradient Background - Spans Both Panels */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 25%, #0369a1 50%, #059669 75%, #10b981 100%)',
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />

        {/* Gradient Overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/10" />

        {/* Content Grid - Both Panels on Same Background */}
        <div className="relative z-10 flex flex-col lg:flex-row h-full min-h-screen">

          {/* LEFT CONTENT */}
          <div className="lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 lg:py-20">

            {/* Logo */}
            <div className="mb-8 lg:mb-12">
              <img
                src="/images/Logo.svg"
                alt="JobAspirant"
                className="h-16 sm:h-20 w-auto"
              />
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Land Your
              <br />
              <span className="text-white/90">Dream Job</span>
              <br />
              <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">Faster</span>
            </h1>

            {/* Value Proposition */}
            <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-md leading-relaxed">
              Whether you're in tech, marketing, finance, healthcare, or legal - see exactly what makes your resume get noticed.
            </p>

            {/* Enhanced Copy - Before CTA */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 mb-6 border border-white/20 max-w-md">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                The Market Has Shifted.
              </h3>
              <p className="text-sm text-white/70 mb-3 leading-relaxed">
                <span className="text-yellow-300 font-semibold">More than 70% of companies</span> now use skills-based hiring.
                Yet <span className="text-yellow-300 font-bold">42% of hiring managers</span> can't find the right talent.
              </p>
              <p className="text-base font-semibold text-white italic">
                "Don't just apply. Qualify."
              </p>
            </div>

            {/* Google Sign In - Main CTA */}
            <div className="mb-8">
              {error && (
                <div className="mb-4 p-3 bg-white/10 backdrop-blur-sm border border-red-300/30 rounded-lg flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-red-300" />
                  <span className="text-sm text-red-200">{error}</span>
                </div>
              )}

              <button
                onClick={() => handleSocialLogin('Google')}
                disabled={loading}
                className="group flex items-center justify-center space-x-3 w-full sm:w-auto px-8 py-4 bg-white text-gray-800 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Continue with Google</span>
                    <Icon name="ArrowRight" size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>

            {/* Feature Highlights - Mobile Only */}
            <div className="lg:hidden space-y-3 mb-8">
              <div className="flex items-center space-x-3 text-white/80">
                <Icon name="CheckCircle" size={20} className="text-green-300" />
                <span className="text-sm">See what recruiters are looking for</span>
              </div>
              <div className="flex items-center space-x-3 text-white/80">
                <Icon name="CheckCircle" size={20} className="text-green-300" />
                <span className="text-sm">Personalized learning roadmap</span>
              </div>
              <div className="flex items-center space-x-3 text-white/80">
                <Icon name="CheckCircle" size={20} className="text-green-300" />
                <span className="text-sm">Interview preparation tips</span>
              </div>
            </div>

            {/* GIF Display - Mobile Only */}
            <div className="lg:hidden mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20 shadow-lg">
                <img
                  src="/images/jobaspirant-demo.gif"
                  alt="JobAspirant Features Walkthrough"
                  className="w-full h-auto rounded-lg max-h-[250px] object-contain"
                />
              </div>
            </div>

            {/* Trust Badge */}
            <div className="flex items-center space-x-2 text-white/60 text-sm">
              <Icon name="Shield" size={16} />
              <span>Secure sign-in powered by Google</span>
            </div>
          </div>

          {/* RIGHT CONTENT - GIF Display */}
          <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-4">

            {/* GIF Container with Glass Effect - Full Width */}
            <div className="w-full h-full flex items-center justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/20 shadow-2xl">
                <img
                  src="/images/jobaspirant-demo.gif"
                  alt="JobAspirant Features Walkthrough"
                  className="w-full h-auto rounded-xl"
                  onError={(e) => {
                    // Fallback placeholder if GIF doesn't exist
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback when GIF is not found */}
                <div className="hidden flex-col items-center justify-center w-full h-[400px] bg-white/5 rounded-xl">
                  <div className="text-6xl mb-4">🎯</div>
                  <div className="text-xl font-bold text-white mb-2">See JobAspirant in Action</div>
                  <div className="text-sm text-white/70 text-center">
                    Upload your GIF to:<br />
                    <code className="bg-white/10 px-2 py-1 rounded text-xs mt-1 inline-block">/images/jobaspirant-demo.gif</code>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* Why JobAspirant + Demo Video Section */}
      <div className="bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">

          {/* Two Column Layout - Text Left, Video Right */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12 mb-12">

            {/* Left Column - Text Content */}
            <div className="lg:w-1/2 text-center lg:text-left mb-8 lg:mb-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Why JobAspirant?
              </h2>
              <p className="text-lg text-muted-foreground mb-6 max-w-lg mx-auto lg:mx-0">
                Real insights that give you an unfair advantage in your job search
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-left">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Skills gap identification & roadmap</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Compare yourself to what hiring managers want</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Hyper-contextualized interview prep</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Resume tips for specific job role</span>
                </div>
              </div>
            </div>

            {/* Right Column - Demo Video */}
            <div className="lg:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl bg-gray-900">
                {/* Vimeo Embed */}
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe
                    src="https://player.vimeo.com/video/1153347274?badge=0&autopause=0&player_id=0&app_id=58479"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    title="JobAspirant Demo"
                  />
                </div>
                {/* Video Caption */}
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 text-center">
                  <p className="text-sm text-muted-foreground">
                    See how JobAspirant helps you land your dream job
                  </p>
                </div>
              </div>
            </div>
          </div>

          <TrustIndicators />
        </div>
      </div>

      <Footer />
      <CookieConsent />

      {/* CSS Animation for Gradient */}
      <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default UserLogin;